import { assertUnreachable } from "@aprao/common/src/assertUnreachable.js";
import { FRACTION_ZERO } from "@aprao/common/src/math/fraction.js";
import { formatDecimalNumber } from "@aprao/common/src/numberFormats.js";
import { salesGroupPredicate } from "@aprao/model/src/shapes/ShapeRevenueGroup.js";
import type { AppraisalDocumentFacade } from "@aprao/model/src/stores/AppraisalDocumentFacade.js";
import type { SalesTaxConfigurationFacade } from "@aprao/model/src/stores/SalesTaxConfigurationFacade.js";
import type { TaxFacade } from "@aprao/model/src/stores/TaxFacade.js";
import { observer } from "mobx-react-lite";
import { mapForNullable } from "option-t/nullable";

import { useI18n } from "../../../common/ApraoI18n.js";
import { useNumberUnits } from "../../../common/useNumberUnits.js";
import { buildRevenueGroupName } from "../../../i18n/buildRevenueGroupName.js";
import { buildSalesTaxName } from "../../../i18n/buildSalesTaxName.js";
import { Section } from "../../Section.js";
import { SimpleTableRow, SimpleTableSection } from "../../SimpleTableRow.js";
import { OperatedAssetTableSection } from "./OperatedAssetTableSection.js";
import { OtherTableSection } from "./OtherTableSection.js";
import { ResidentialTableSection } from "./ResidentialTableSection.js";

export const RevenueBreakdown = observer(function RevenueBreakdown(props: {
  appraisal: AppraisalDocumentFacade;
  salesTaxConfiguration: SalesTaxConfigurationFacade | null;
  tax: TaxFacade;
}) {
  const { appraisal, salesTaxConfiguration, tax } = props;

  const { msg } = useI18n();
  const { lengthUnit, currency } = useNumberUnits();

  return (
    <Section title="Revenue Breakdown">
      <SimpleTableSection>
        <SimpleTableRow
          isHeader
          cells={[
            "Summary",
            "Quantity",
            `${lengthUnit.symbol}² (${msg("global.nia")})`,
            `${currency.symbol}/${lengthUnit.symbol}²`,
            "Ave Value",
            `Total (${currency.symbol})`,
          ]}
        />
        {appraisal.revenueGroups.map((group, idxGroup) =>
          salesGroupPredicate(group) ?
            <SimpleTableRow
              key={idxGroup}
              cells={[
                buildRevenueGroupName(
                  group.$model,
                  appraisal.$model.stages.revenue.revenueGroups,
                ),
                group.totalCount,
                formatDecimalNumber(group.areaTotal ?? FRACTION_ZERO),
                mapForNullable(
                  group.averageValuePerArea,
                  formatDecimalNumber,
                ) ?? "N/A",
                mapForNullable(group.meanValue, formatDecimalNumber) ?? "N/A",
                formatDecimalNumber(group.valueGross ?? FRACTION_ZERO),
              ]}
            />
          : <SimpleTableRow
              key={idxGroup}
              cells={[
                buildRevenueGroupName(
                  group.$model,
                  appraisal.$model.stages.revenue.revenueGroups,
                ),
                group.totalCount,
                "N/A",
                "N/A",
                "N/A",
                formatDecimalNumber(group.valueGross ?? FRACTION_ZERO),
              ]}
            />,
        )}
        <SimpleTableRow
          bold
          borderBottom
          borderTop
          cells={[
            msg("global.grossDevelopmentValue"),
            null,
            formatDecimalNumber(appraisal.salesAreaTotal ?? FRACTION_ZERO),
            null,
            null,
            formatDecimalNumber(
              appraisal.stages.revenue.getGrossDevelopmentValue("any") ??
                FRACTION_ZERO,
            ),
          ]}
        />
        <SimpleTableRow
          cells={[
            "Costs",
            null,
            null,
            null,
            null,
            formatDecimalNumber(appraisal.salesCostTotal ?? FRACTION_ZERO),
          ]}
        />
        {salesTaxConfiguration && (
          <SimpleTableRow
            cells={[
              buildSalesTaxName(salesTaxConfiguration.$model),
              null,
              null,
              null,
              null,
              formatDecimalNumber(tax.gdvSalesTaxOnAmount ?? FRACTION_ZERO),
            ]}
          />
        )}
        <SimpleTableRow
          bold
          borderBottom={!appraisal.hasAdditionalIncome}
          borderTop
          thickerBorder
          cells={[
            msg("global.netDevelopmentValue"),
            null,
            null,
            null,
            null,
            formatDecimalNumber(tax.netDevelopmentValue ?? FRACTION_ZERO),
          ]}
        />
        {appraisal.hasAdditionalIncome && (
          <>
            <SimpleTableRow
              bold
              borderTop
              thickerBorder
              cells={[
                "Additional income",
                null,
                null,
                null,
                null,
                formatDecimalNumber(
                  appraisal.getAdditionalIncomeTotal("any") ?? FRACTION_ZERO,
                ),
              ]}
            />
            <SimpleTableRow
              bold
              borderTop
              borderBottom
              thickerBorder
              cells={[
                msg("global.netRevenue"),
                null,
                null,
                null,
                null,
                formatDecimalNumber(tax.netRevenue ?? FRACTION_ZERO),
              ]}
            />
          </>
        )}
      </SimpleTableSection>
      {appraisal.revenueGroups.map((group, groupIdx) => {
        const label = `${buildRevenueGroupName(
          group.$model,
          appraisal.$model.stages.revenue.revenueGroups,
        )}${group.fundingType === "forwardFunded" ? " (forward funded)" : ""}`;

        return (
          group.category === "residential" ?
            <ResidentialTableSection
              key={groupIdx}
              label={label}
              revenueGroup={group}
              salesTaxConfiguration={salesTaxConfiguration}
              tax={tax}
            />
          : group.category === "operated" ?
            <OperatedAssetTableSection
              key={groupIdx}
              label={label}
              revenueGroup={group}
              salesTaxConfiguration={salesTaxConfiguration}
              tax={tax}
            />
          : group.category === "other" ?
            <OtherTableSection
              key={groupIdx}
              label={label}
              revenueGroup={group}
              salesTaxConfiguration={salesTaxConfiguration}
              tax={tax}
            />
          : assertUnreachable(group)
        );
      })}
    </Section>
  );
});
